import { useState, useCallback } from 'react';
import { User } from 'firebase/auth';
import { useSlashCommandMenu } from './use-slash-command-menu';
import { BodyParagraphData, EssayPlanItem } from '@/types/writing';
import { Task } from '@/app/task-bank/public-actions';
import {
  CONCLUSION_GUIDELINES,
  TASK2_INTRO_GUIDELINE,
} from '@/lib/prompts/guidelines';
import {
  SlashTriggerContext,
  BaseSlashCommandContext,
  CommandParams,
  NowWhatParams,
  FindSynonymParams,
  FindExampleParams,
  MotJusteParams,
  CheckTopicSentenceParams,
  CheckIntroductionParams,
  CheckConclusionParams,
  CommandResult,
  CommandContext,
} from '@/types/slash-commands';
import { COMMANDS } from '@/lib/slash-commands/registry';

interface UseEssaySlashCommandManagerProps {
  introduction: string;
  bodyParagraphs: BodyParagraphData[];
  conclusion: string;
  essayPlan: EssayPlanItem[];
  prompt: string;
  user: User | null;
  selectedTask: Task | null;
  taskType: 'task1' | 'task2';
}

export function useEssaySlashCommandManager({
  introduction,
  bodyParagraphs,
  conclusion,
  essayPlan,
  prompt,
  user,
  selectedTask,
  taskType,
}: UseEssaySlashCommandManagerProps) {
  const slashCommandMenu = useSlashCommandMenu(taskType);
  const {
    closeMenu: closeSlashMenu,
    triggerContext: currentSlashTriggerContext,
    isMenuOpen: isSlashMenuOpenHook,
    menuTargetElement,
    filteredCommands,
    selectedIndex,
    setSelectedIndex,
    openMenu,
    updateFilter,
    handleMenuKeyDown,
  } = slashCommandMenu;

  const [isCommandResultPopoverOpen, setIsCommandResultPopoverOpen] =
    useState(false);
  const [isCommandLoading, setIsCommandLoading] = useState(false);
  const [commandResult, setCommandResult] = useState<CommandResult | null>(
    null
  );
  const [commandError, setCommandError] = useState<string | null>(null);
  const [commandResultTargetElement, setCommandResultTargetElement] =
    useState<HTMLElement | null>(null);
  const [activeCommandId, setActiveCommandId] = useState<string | null>(null);

  const closeCommandResultPopover = useCallback(() => {
    setIsCommandResultPopoverOpen(false);
    setCommandResult(null);
    setCommandError(null);
    setIsCommandLoading(false);
    setCommandResultTargetElement(null);
    setActiveCommandId(null);
  }, []);

  const executeSlashCommand = useCallback(
    async (commandId: string) => {
      const triggerContext = currentSlashTriggerContext;

      if (!triggerContext || !user) {
        console.error(
          'Cannot execute command: Missing trigger context or user.'
        );
        closeSlashMenu();
        return;
      }

      const command = COMMANDS.find((cmd) => cmd.id === commandId);

      if (!command) {
        console.error(`Command with ID "${commandId}" not found in registry.`);
        setCommandError(`Unknown command: ${commandId}`);
        setCommandResult(null);
        setIsCommandResultPopoverOpen(true);
        setCommandResultTargetElement(triggerContext.targetElement);
        setActiveCommandId(commandId);
        closeSlashMenu();
        return;
      }

      setActiveCommandId(commandId);
      setCommandResultTargetElement(triggerContext.targetElement);
      setIsCommandLoading(true);
      setCommandError(null);
      setCommandResult(null);
      setIsCommandResultPopoverOpen(true);
      closeSlashMenu();

      const baseContext: BaseSlashCommandContext = {
        userId: user.uid,
        essayData: {
          type: 'essay',
          introduction,
          bodyParagraphs,
          conclusion,
        },
        prompt,
        essayPlan,
        questionType: selectedTask?.questionType ?? '',
        questionNote: selectedTask?.questionNote ?? '',
        hasMultipleParts: selectedTask?.hasMultipleParts ?? false,
      };

      let commandParams: CommandParams | null = null;
      try {
        switch (commandId) {
          case 'nowWhat':
            commandParams = {
              currentParagraphIndex: triggerContext.currentParagraphIndex,
              currentParagraphType: triggerContext.currentParagraphType,
              textBeforeTrigger: triggerContext.textBeforeTrigger,
            } as NowWhatParams;
            break;
          case 'findSynonym':
            if (!triggerContext.wordBeforeTrigger)
              throw new Error('Word before trigger is missing.');
            commandParams = {
              wordToReplace: triggerContext.wordBeforeTrigger,
              sentenceContext: triggerContext.textBeforeTrigger,
            } as FindSynonymParams;
            break;
          case 'findExample':
            commandParams = {
              textBeforeTrigger: triggerContext.textBeforeTrigger,
            } as FindExampleParams;
            break;
          case 'motJuste':
            commandParams = {
              textBeforeTrigger: triggerContext.textBeforeTrigger,
            } as MotJusteParams;
            break;
          case 'checkTopicSentence':
            if (!triggerContext.firstSentenceOfParagraph)
              throw new Error('First sentence is missing.');
            commandParams = {
              topicSentence: triggerContext.firstSentenceOfParagraph,
            } as CheckTopicSentenceParams;
            break;
          case 'checkIntroduction':
            commandParams = {
              introductionText: introduction,
              guideline: TASK2_INTRO_GUIDELINE,
            } as CheckIntroductionParams;
            break;
          case 'checkConclusion':
            commandParams = {
              conclusionText: conclusion,
              guideline: CONCLUSION_GUIDELINES,
            } as CheckConclusionParams;
            break;
          case 'checkOverview':
            commandParams = {
              conclusionText: conclusion,
              guideline: '', // Not used, handled in action
            } as CheckConclusionParams;
            break;
          default:
            throw new Error(
              `Parameter mapping not implemented for command: ${commandId}`
            );
        }
      } catch (paramError: unknown) {
        console.error(
          `Error constructing params for command ${commandId}:`,
          paramError
        );
        const message =
          paramError instanceof Error
            ? paramError.message
            : 'Unknown parameter error';
        setCommandError(`Internal Error: ${message}`);
        setIsCommandLoading(false);
        return;
      }

      const fullCommandContext: CommandContext<CommandParams> & {
        taskType: string;
      } = {
        ...baseContext,
        commandParams: commandParams,
        taskType,
      };

      try {
        console.log(
          `Executing command "${commandId}" with context:`,
          fullCommandContext
        );
        const result: CommandResult = await command.execute(fullCommandContext);
        console.log(`Command "${commandId}" result:`, result);

        if (result.error) {
          setCommandError(result.error);
          setCommandResult(null);
        } else {
          setCommandResult(result);
          setCommandError(null);
        }
      } catch (executionError: unknown) {
        console.error(`Error executing command ${commandId}:`, executionError);
        const message =
          executionError instanceof Error
            ? executionError.message
            : 'Unknown execution error';
        setCommandError(`Execution Error: ${message}`);
        setCommandResult(null);
      } finally {
        setIsCommandLoading(false);
      }
    },
    [
      currentSlashTriggerContext,
      user,
      introduction,
      bodyParagraphs,
      conclusion,
      essayPlan,
      prompt,
      selectedTask,
      closeSlashMenu,
      taskType,
    ]
  );

  const handleTextAreaKeyDownDirect = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      handleMenuKeyDown(event, executeSlashCommand);
    },
    [handleMenuKeyDown, executeSlashCommand]
  );

  const handleSlashTrigger = useCallback(
    (context: SlashTriggerContext) => {
      openMenu(context);
    },
    [openMenu]
  );

  const handleSlashFilterUpdate = useCallback(
    (filterText: string) => {
      updateFilter(filterText);
    },
    [updateFilter]
  );

  const handleSlashClose = useCallback(() => {
    closeSlashMenu();
  }, [closeSlashMenu]);

  return {
    isSlashMenuOpen: isSlashMenuOpenHook,
    slashMenuTargetElement: menuTargetElement,
    slashMenuFilteredCommands: filteredCommands,
    slashMenuSelectedIndex: selectedIndex,
    setSlashMenuSelectedIndex: setSelectedIndex,
    slashMenuTriggerContext: currentSlashTriggerContext,
    handleSlashClose,
    handleSlashCommandTextareaKeyDown: handleTextAreaKeyDownDirect,
    handleSlashTrigger,
    handleSlashFilterUpdate,
    executeSlashCommand,
    isCommandResultPopoverOpen,
    isCommandLoading,
    commandResult,
    commandError,
    commandResultTargetElement,
    closeCommandResultPopover,
    activeCommandId,
  };
}
